import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormControl} from '@angular/forms';
import {startWith, takeUntil} from 'rxjs/operators';
import {MatSelectChange} from '@angular/material/select';
import {Contract} from '../../interface/contract';
import {Observable, ReplaySubject} from 'rxjs';
import {PartnerService} from '../../services/partner.service';
import {ListResponse} from '../../interface/response/list-response';
import {ContractService} from '../../services/contract.service';

@Component({
  selector: 'app-dict-contracts',
  templateUrl: './dict-contracts.component.html',
  styleUrls: ['./dict-contracts.component.scss']
})
export class DictContractsComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() label: string;
  @Input() multiple = false;
  @Input() control: FormControl = null;
  @Input() required = false;
  @Input() contracts: ReplaySubject<Contract[]>;
  @Input() partnerId: Observable<string>;
  @Input() companyId: number;
  @Input() contractType?: string;
  @Input() excludeId?: string;
  @Input() showOwnerName = false;
  @Input() showStatus = false;
  @Input() initializeWith = null;
  @Input() filterPartnership = false;
  @Input() partnerContractsChange = false;
  @Output() selectedContract: EventEmitter<Contract> = new EventEmitter<Contract>();

  externalControl = true;
  disabled = false;
  items: Contract[] = [];
  value?: number = null;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private partnerService: PartnerService,
    private contractService: ContractService,
  ) {
    if (!this.contracts) {
      this.contracts = new ReplaySubject<Contract[]>(1);
      this.contracts.next([]);
    }
  }

  onChange: any = () => { };
  onTouched: any = () => { };

  ngOnInit(): void {
    this.contracts
      .pipe(takeUntil(this.destroyed$))
      .subscribe((items: Contract[]) => {
        const contracts = [];
        items.forEach((el: Contract) => {
          let valid = true;

          if (this.contractType && this.contractType !== el.type.key) {
            valid = false;
          }

          if (this.excludeId === el.uuid) {
            valid = false;
          }

          if (this.companyId && this.companyId !== el.company.id) {
            valid = false;
          }

          if (valid) {
            contracts.push(el);
          }
        });

        this.items = contracts;
      });

    if (this.control === null) {
      this.control = new FormControl('');
      this.externalControl = false;
    }

    if (this.partnerId) {
      const partnerContractsSubject: ReplaySubject<ListResponse<Contract>> = new ReplaySubject<ListResponse<Contract>>(1);
      partnerContractsSubject
        .pipe(takeUntil(this.destroyed$))
        .subscribe((response: ListResponse<Contract>) => {
          this.contracts.next(response.items);
        });

      this.partnerId
        .pipe(
          takeUntil(this.destroyed$),
          startWith(this.initializeWith)
        ).subscribe((partnerId: string) => {
          if (partnerId) {
            this.contractService.loadNotCountedList(0, 9999, {
              partner: partnerId,
              onlyPartnership: this.filterPartnership ? true : null,
              partnershipContractTypesChange: this.partnerContractsChange === null ? null : this.partnerContractsChange,
            }, partnerContractsSubject);
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.externalControl) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.disabled = isDisabled;
  }

  writeValue(value: any): void {
    if (this.externalControl) {
      return;
    }

    this.control.setValue(value);
    this.value = value;
  }

  onSelectionChange(e: MatSelectChange): void {
    this.items.forEach((contract: Contract) => {
      if (contract.uuid === e.value) {
        this.selectedContract.emit(contract);
      }
    });

    this.value = e.value;
    this.onChange(this.value);
  }

  clearValue(): void {
    this.control.setValue(this.multiple ? [] : '');
    this.writeValue(this.multiple ? [] : '');
    this.onChange(this.value);
  }
}
